import React, { useEffect } from 'react';
import TextareaField from '@/components/form/fields/TextareaField';
import MDXEditorField from '@/components/form/fields/mdx/MDXEditorField';
import CodeMirrorField from '@/components/form/fields/CodeMirrorField';
import { Note } from '@/store/notesStore';
import { useNoteContext } from '@/components/notes/context/NoteContext';
import { setCurrentNoteId } from '@/components/form/fields/mdx/plugins/secretFieldPlugin/secretFieldContext';

interface NoteContentProps {
  note: Note;
  content: string;
  onChange: (value: string) => void;
  className?: string;
  rows?: number;
  autoFocus?: boolean;
  placeholder?: string;
  toolbarPortalTarget?: HTMLElement | null;
}

const NoteContent: React.FC<NoteContentProps> = ({
  note,
  content,
  onChange,
  className = '',
  rows = 8,
  autoFocus = false,
  placeholder = 'Write your note content here...',
  toolbarPortalTarget,
}) => {
  const { noteId, isReadOnly } = useNoteContext();

  // Keep the module-level note ID in sync so the Lexical export visitor
  // (which cannot use React context) knows which note key to look up.
  useEffect(() => {
    setCurrentNoteId(noteId);
    return () => setCurrentNoteId(null);
  }, [noteId]);

  const contentType = note.content_type || 'TEXT';

  switch (contentType) {
    case 'MDX':
      return (
        <MDXEditorField
          value={content}
          onChange={onChange}
          placeholder={placeholder}
          className={className}
          rows={rows}
          autoFocus={autoFocus}
          toolbarPortalTarget={toolbarPortalTarget}
          readOnly={isReadOnly}
        />
      );

    case 'TXT':
      return (
        <CodeMirrorField
          value={content}
          onChange={onChange}
          placeholder={placeholder}
          className={className}
          autoFocus={autoFocus}
          toolbarPortalTarget={toolbarPortalTarget}
          readOnly={isReadOnly}
        />
      );

    default:
      return (
        <TextareaField
          value={content}
          onChange={onChange}
          placeholder={placeholder}
          className={className}
          autoFocus={autoFocus}
          readOnly={isReadOnly}
        />
      );
  }
};

export default NoteContent;
